/***
 * Created on 2025/5/9
 * @author: Adam Cheng <adamcheng@qnap.com>
 ***/

import React, {useReducer} from 'react';
import ModalComponent from "../common/ModalComonent/ModalComponent";
import {useTranslation} from "react-i18next";
import TableComponent from "../common/TableComponent/TableComponent";
import {dataCellLabelToDataGridArea, normalizeSpaceSize} from "../../service/utils";
import UserAvatarIcon from "../common/UserAvatarIcon";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {useOrganizationApi} from "../../hook/OrganizationAPIHook";
import {useOrganization} from "../../context/OrganizationContext";
import {invalidateAccessGrant} from "../../service/accessGrantSync";
import OrgContentPlaceholderComponent from "../organization-content/OrgContentPlaceholderComponent";
import UserStatusComponent from "../common/UserStatusComponent";
import {useAlertContext} from "../../context/AlertContext";

const reducerHandler = (state, action) => {
    switch (action.type) {
         case 'update_data':
            return {
                ...state,
                data: action.payload.data
            }
        case 'select_all':
            const selected = state.data.map((item) => {item.checked = true; return item});
            return {
                ...state,
                data: selected,
                select_box: true,
                input: state.input + 1
            }
        case 'select_none':
            const unSelected = state.data.map((item) => {item.checked = false; return item});
            return {
                ...state,
                data: unSelected,
                select_box: false,
                input: state.input + 1
            }
        case 'toggle_checkbox':
            const updatedData = state.data.map((item) => {
                if (item.user_id === action.payload.user_id) {
                    return {...item, checked: !item.checked};
                }
                return item;
            });
            let check_status = updatedData?.every((o) => o.checked);
            if (!check_status) {
                check_status = updatedData?.some((o) => o.checked) ? 'partial' : false;
            }
            return {
                ...state,
                data: updatedData,
                select_box: check_status,
                input: state.input + 1
            }
        default:
            return state;
    }
}

function CloudSpaceLimitStorageUserRow({table_head_fields, row_data, index, doRefresh, disPatcher, table_ui, isOwner, permissions, show_checkbox=false}) {
    const { t } = useTranslation();

    const toggle_checkbox = () => {
        disPatcher({type:'toggle_checkbox', payload: row_data});
    }

    const usage = row_data?.quota_usages?.filter((o) => o.space_kind === 'org_personal')?.reduce((total, u) => total + u.usage, 0) || null;

    return (
        <div className="flexbox-trow" key={index}>
            <div className="tbody-cell select-cell">
                <div className={`comp-checkbox no-label ${show_checkbox ? 'visible-checkbox' : ''}`}>
                    <input type="checkbox" id={`cb-${index}`}
                           checked={row_data.checked || false}
                           onChange={() => toggle_checkbox(row_data)}
                    />
                    <label htmlFor={`cb-${index}`} className="comp-label"/>
                </div>
            </div>
            <div className={`tbody-cell ${table_head_fields[0].width_style}`} data-cell-label={t(table_head_fields[0].field_name)} data-grid-area={dataCellLabelToDataGridArea(table_head_fields[0].field_name)}>
                <div className="admin-info">
                    <UserAvatarIcon user_info={row_data} with_user_info={true} avatar_class='admin'/>
                </div>
            </div>
            <div className={`tbody-cell ${table_head_fields[1].width_style}`} data-cell-label={t(table_head_fields[1].field_name)} data-grid-area={dataCellLabelToDataGridArea(table_head_fields[1].field_name)}>
                {usage ? normalizeSpaceSize(usage) :  (row_data?.seated_regions ? normalizeSpaceSize(0) : '-')}
            </div>
            <div className={`tbody-cell ${table_head_fields[2].width_style}`} data-cell-label={t(table_head_fields[2].field_name)} data-grid-area={dataCellLabelToDataGridArea(table_head_fields[2].field_name)}>
                <UserStatusComponent user={row_data}/>
            </div>
        </div>
    );
}

function CloudSpaceLimitStorageUserModal({isOpen, onClose, unrestricted_users = [], usage_data}) {
    const {t} = useTranslation();
    const {OrganizationAPI} = useOrganizationApi();
    const {getCurrentOrg} = useOrganization();
    const currentUser = JSON.parse(window.localStorage.getItem('currentUser'));
    const org = getCurrentOrg();
    const queryClient = useQueryClient();
    const alert = useAlertContext();

    const table_head_fields = [
        {field_name: t('@QNAP ID (Email Address)@'), isSortable: true, width_style: '!w-55', field_id: 'display_name', default_sort: true, default_sort_order: ''},
        {field_name: t('@Personal Space Usage@'), isSortable: false, width_style: '!w-25', info:{show_info: true, info_text: t('@Storage used in the Personal Space of this user only. Shared Space usage is not included.@')}},
        {field_name: t('@Status@'), isSortable: false, width_style: '!w-20'}
    ];

    const {data, isFetched} = useQuery({
        queryKey: ['restricted_storage_users', 'seated=true', usage_data?.exception_user_ids?.length],
        queryFn: async (data) => {
            const resp = await OrganizationAPI.q2.get(`org_storage/${org.organization_id}/user?seated=true`);
            const exception_user_ids = usage_data?.exception_user_ids?.map((item) => item.id);
            const orgUsers = org?.users || [];
            const result = resp.result.filter(item=>orgUsers.find((u) => u.id === item.user_id)).map((item) => {
                const user = orgUsers.find((u) => u.id === item.user_id);
                return {
                    ...item,
                    status: user.status,
                    id: item.user_id,
                }
            }).filter(item => !exception_user_ids?.includes(item.user_id));
            dispatch({type: 'update_data', payload: {data: result}});
            return result;
        },
    });

    const updateRestrictUserMutatation = useMutation({
        mutationFn: async (data) => await OrganizationAPI.q2.put(`org_storage/${org.organization_id}/space_limit`, {}, data),
        onSuccess: async (data, variables) => {
            // await queryClient.invalidateQueries({queryKey: ['unrestricted_storage_users_with_seated'], refetchType: 'all'});
            const activity_data = {
                organization_id: org.organization_id,
                user_id: currentUser.user_id,
                event: 25,
                category: 6,
                meta_data: {data: variables}
            }
            await OrganizationAPI.put('organization_activity',null, activity_data);
            onClose();
            alert(true, 'success', 0, t('@User added to the Unrestricted storage list successfully.@'), 10000);
            invalidateAccessGrant(queryClient, org.organization_id);
        },
        onError: (error) => {
            console.log('Update user limit error', error);
            alert(true, 'error', 0, 'Failed to add user to the Unrestricted storage list. Please try again later.');
        }
    });

    const updateRestrictUser = async () => {
        const selected_users = state.data.filter((item) => item.checked);
        const selected_users_data = selected_users.map((item) => ({id: item.user_id, qid: item.email}));
        const exception_user_ids = usage_data?.exception_user_ids?.map((item) => item.id) || [];
        const payload = {
            personal_max_size: usage_data.personal_max_size,
            ignore_warning: true,
            exception_user_ids: exception_user_ids.concat(selected_users.map(user => user.user_id)),
            selected_users_data
        }
        await updateRestrictUserMutatation.mutateAsync(payload);
    }

    const reducerInitializer = () => {
        return {
            select_box: false,
            show_warning: false,
            show_popover: false,
            data: data,
            input: 1
        };
    }

    const [state, dispatch] = useReducer(reducerHandler, {}, reducerInitializer);

    return (
        <ModalComponent title={t('@Unrestricted storage users@')} isOpen={isOpen} onClose={onClose} okText={t('@Apply@')} input={state.input} preSubmit={() => state.select_box} onSubmit={updateRestrictUser} >
            <div className="dialog-content">
                <div className="text-wrap">
                    {t('@Designate members with unrestricted storage.@')}
                </div>
                <div className="text-wrap !mb-24">
                    {t('@Unrestricted removes the Personal Space limit; usage still counts toward the organization total and may reduce what remains for others.@')}
                </div>

                {!isFetched && <OrgContentPlaceholderComponent show_partial={true}/>}

                {
                    isFetched && state?.data?.length <= 0 &&
                    <div className="flexbox-table">
                        <div className="table-empty">
                            <div className="empty-msg">{t('@No members yet@')}</div>
                        </div>
                    </div>
                }

                {
                    isFetched && state?.data?.length > 0 &&
                    <TableComponent
                        row_component={CloudSpaceLimitStorageUserRow}
                        table_head_fields={table_head_fields}
                        table_data={state}
                        total_record={0}
                        doRefresh={() => {}}
                        disPatcher={dispatch}
                        table_ui={{table_field_checkbox:true}}
                        search={false}
                        empty_data={null}
                        pagination={false}
                        data_table_name="unrestricted-storage-user-table"
                    />
                }
            </div>
        </ModalComponent>
    );
}

export default CloudSpaceLimitStorageUserModal;